import { ArrowRight, Check } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { SubscriptionPlan, SubscriptionStatusResponse } from '@/types/api/subscription'
import { formatLimitValue, isBooleanLimit } from '@/utils/quota'

interface PlanConfirmDialogProps {
  plan: SubscriptionPlan | null
  currentStatus: SubscriptionStatusResponse | null | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
  isPending: boolean
}

export function PlanConfirmDialog({
  plan,
  currentStatus,
  open,
  onOpenChange,
  onConfirm,
  isPending,
}: PlanConfirmDialogProps) {
  if (!plan) return null

  const hasExistingSubscription = !!currentStatus?.subscriptionId
  const isFreePlan = plan.price === 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold tracking-tight text-foreground">
            {hasExistingSubscription ? 'Change Subscription Plan' : 'Confirm Subscription'}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {hasExistingSubscription
              ? 'Review the new plan details before applying changes to your workspace.'
              : 'Review your selected plan and confirm to activate your subscription.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Plan Transition Pill */}
          <div className="flex items-center justify-between rounded-xl border border-border bg-muted/50 p-3 text-sm">
            <div className="flex flex-col">
              <span className="text-xs font-medium text-muted-foreground">Current</span>
              <span className="font-semibold text-foreground">
                {currentStatus?.planName || 'None'}
              </span>
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
            <div className="flex flex-col text-right">
              <span className="text-xs font-medium text-muted-foreground">Target Plan</span>
              <span className="font-semibold text-primary">
                {plan.name}
              </span>
            </div>
          </div>

          {/* Pricing & Duration */}
          <div className="flex items-center justify-between rounded-xl border border-border bg-card p-4">
            <div>
              <p className="text-xs text-muted-foreground">Plan Billing</p>
              <div className="flex items-baseline gap-1 mt-0.5">
                <span className="text-2xl font-bold tabular-nums text-foreground">
                  ₹{plan.price}
                </span>
                <span className="text-xs text-muted-foreground">
                  /{plan.durationMonths}mo
                </span>
              </div>
            </div>
            {isFreePlan && (
              <Badge variant="secondary">Free</Badge>
            )}
          </div>

          {/* Key Entitlements summary */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Included Plan Entitlements:</p>
            <div className="max-h-40 overflow-y-auto rounded-lg border border-border/80 bg-background/50 p-3">
              <ul className="space-y-2 text-xs">
                {plan.entitlements
                  .filter((e) => String(e.limitValue).toLowerCase() !== 'false')
                  .slice(0, 5)
                  .map((e) => (
                    <li key={e.featureKey} className="flex items-center gap-2 text-foreground">
                      <Check className="h-3.5 w-3.5 shrink-0 text-success-600" />
                      <span>
                        {isBooleanLimit(e.limitType)
                          ? e.name
                          : `${formatLimitValue(e.featureKey, e.limitValue)} ${e.name}`}
                      </span>
                    </li>
                  ))}
                {plan.entitlements.length > 5 && (
                  <li className="pt-1 text-[11px] text-muted-foreground">
                    + {plan.entitlements.length - 5} additional entitlements
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending
              ? 'Processing...'
              : hasExistingSubscription
                ? 'Confirm Plan Change'
                : 'Activate Plan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
